import { api } from '@/lib/api';

export interface AdminUser {
    id: number;
    email: string;
    roles: string[];
    /** Membership ids; empty = no scoped access (super admins). */
    website_ids: number[];
    /** Membership entries with code+name for display. */
    websites: Array<{ id: number; code: string; name: string }>;
    /** Last password or passkey sign-in (ISO); null = none recorded yet. */
    last_login_at: string | null;
}

export async function fetchUsers(): Promise<{ items: AdminUser[] }> {
    return api('/api/admin/users');
}

export async function fetchUser(id: number): Promise<AdminUser> {
    return api(`/api/admin/users/${id}`);
}

export async function createUser(payload: {
    email: string;
    password: string;
    role: string;
    website_ids?: number[];
}): Promise<AdminUser> {
    return api('/api/admin/users', { method: 'POST', body: payload });
}

export async function updateUser(id: number, payload: {
    email?: string;
    role?: string;
    website_ids?: number[];
    password?: string;
}): Promise<AdminUser> {
    return api(`/api/admin/users/${id}`, { method: 'PATCH', body: payload });
}

export async function deleteUser(id: number): Promise<void> {
    await api(`/api/admin/users/${id}`, { method: 'DELETE' });
}

/** Token pair issued by the (stop-)impersonate endpoints. */
interface ImpersonateTokens {
    access_token: string;
    refresh_token: string;
}

/**
 * Mint a token pair for `id` as the impersonated user. Super-only on the
 * server. The issued access token carries an `impersonator_id` claim so
 * /me can surface who's behind the session.
 */
export async function impersonateUser(id: number): Promise<ImpersonateTokens> {
    return api(`/admin/impersonate/${id}`, { method: 'POST' });
}

/**
 * Exchange the current impersonated token for a fresh token pair back as
 * the original (impersonating) super. Requires an `impersonator_id` claim.
 */
export async function stopImpersonating(): Promise<ImpersonateTokens> {
    return api('/admin/stop-impersonate', { method: 'POST' });
}
